import Drawer from '../../components/Drawer';
import ProductForm from './ProductForm';
import ProductImages from './ProductImages';
import { formatMoney } from '@shopify/theme-currency';

const selectors = {
  priceWrapper: '[data-price-wrapper]',
  productPrice: '[data-product-price]',
  comparePrice: '[data-compare-price]',
  comparePriceText: '[data-compare-text]',
  sizeChart: '[data-pdp-size-chart]',
  sizeChartOpenButton: '[data-open-size-guide]',
};

const cssClasses = {
  hidden: 'hidden',
  onSale: 'text-tidal-wave',
};

/**
 * The wrapper class for PDP components
 *
 * @export
 * @class Product
 */
export default class Product {
  /**
   * Creates an instance of Product.
   *
   * @param {HTMLElement} el The container of this product
   * @param {Object} config Settings object
   */
  constructor(el, config = {}) {
    this.el = el;

    this.config = Object.assign({ isQuickShop: false }, config);

    this.drawer = new Drawer();

    this._initComponents();

    const sizeChartOpenButton = el.querySelector(selectors.sizeChartOpenButton);
    if (sizeChartOpenButton) {
      sizeChartOpenButton.addEventListener('click', this._openDrawer(el));
    }
  }

  /**
   * Init all components that make up the product
   */
  _initComponents() {
    this.productImages = new ProductImages(this.el);
    this.productForm = new ProductForm(this.el, {
      onVariantChange: variant => this._onVariantChange(variant),
      isQuickShop: this.config.isQuickShop,
    });
  }

  /**
   * Opens drawer.
   *
   * @param {object} container - Container of form to be opened in the drawer.
   */
  _openDrawer = container => event => {
    const drawer = container.querySelector(selectors.sizeChart);
    this.drawer.open(`#${drawer.getAttribute('id')}`);
  };

  /**
   * Call functions to update on variant change from product form
   *
   * @param {Object} variant
   */
  _onVariantChange = variant => {
    this.productImages.change(variant);
    this._updatePrice(variant);
    this._updateVariantColorLabel(variant);
    this._updateVariantSKU(variant);
    window.dispatchEvent(
      new CustomEvent('variant:update', { detail: { variant } }),
    );
  };

  /**
   * Update prices when selected variant changes
   *
   * @param {Object} variant
   */
  _updatePrice = variant => {
    const comparePrice = this.el.querySelector(selectors.comparePrice);
    const compareText = this.el.querySelector(selectors.comparePriceText);
    const mainPrice = this.el.querySelector(selectors.productPrice);

    const isOnSale = variant.compare_at_price > variant.price;
    const comparePriceText = isOnSale
      ? formatMoney(variant.compare_at_price, theme.moneyFormat)
      : '';

    // Update the product price
    mainPrice.innerHTML = formatMoney(variant.price, theme.moneyFormat);

    // Update compare at price depending on whether variant is on sale
    if (comparePrice && compareText) {
      comparePrice.innerHTML = comparePriceText;
      if (isOnSale) {
        comparePrice.classList.remove(cssClasses.hidden);
        compareText.classList.remove(cssClasses.hidden);
        mainPrice.classList.add(cssClasses.onSale);
      } else {
        comparePrice.classList.add(cssClasses.hidden);
        compareText.classList.add(cssClasses.hidden);
        mainPrice.classList.remove(cssClasses.onSale);
      }
    }
  };

  /**
   * Update color label when selected variant changes
   *
   * @param {Object} variant
   */

  _updateVariantColorLabel(variant) {
    const colorOption = variant.option1;
    const colorLabel = this.el.querySelector('[data-color-label]');
    colorLabel.innerHTML = colorOption;
  }

  /**
   * Update variant SVU label when selected variant changes
   *
   * @param {Object} variant
   */

  _updateVariantSKU(variant) {
    this.el.querySelectorAll('[data-sku-label]').forEach(el => {
      el.innerHTML = variant.sku;
    });
  }
}
